import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogScrollArea,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  CheckCircle,
  Chrome,
  Download,
  Shield,
  Settings,
  Zap,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ChromeExtensionSuccessModalContent } from './ChromeExtensionSuccessModal';
import apiService from '../../../services/api';
import { AdvancedSocketSelection } from '../AdvancedSocketSelection';
import { integrationRegistry } from '../../../integrations/registry';
import { SpaceSocket } from '../../../services/api';
import {
  getOrCreateDefaultSocket,
  defaultExtensionDisplayNameFromSocket,
  isSocketUuid,
} from '../../../utils/socketUtils';
import { buildBindingPermissions } from '../shared/bindingPayload';
import { MemoryPermissionsFields } from '../shared/MemoryPermissionsFields';
import { ContentFiltersFields } from '../shared/ContentFiltersFields';
import { RateLimitFields } from '../shared/RateLimitFields';

interface ChromeExtensionIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateIntegration: (integration: any) => Promise<any>;
  socket?: SpaceSocket;
  spaceId: string;
  existingIntegration?: {
    binding_id: string;
    socket_id: string;
    integration_name: string;
    auth_config?: Record<string, any>;
  } | null;
}

export default function ChromeExtensionIntegrationModal({
  isOpen,
  onClose,
  onCreateIntegration,
  socket,
  spaceId,
  existingIntegration
}: ChromeExtensionIntegrationModalProps) {
  
  const { t } = useTranslation();
  
  // Form state
  const [integrationName, setIntegrationName] = useState('Chrome Extension');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [selectedSocket, setSelectedSocket] = useState<SpaceSocket | null>(
    socket && isSocketUuid(socket.socket_id) ? socket : null
  );
  const [socketLoading, setSocketLoading] = useState(false);
  const [, setSocketError] = useState<string | null>(null);
  
  // Existing integration state
  const [existingBindingId, setExistingBindingId] = useState<string | null>(null);
  const [isLoadingExtension, setIsLoadingExtension] = useState(false);
  const [extensionStatus, setExtensionStatus] = useState<'available' | 'expired' | 'not_found' | null>(null);

  // Get or create default socket when modal opens
  useEffect(() => {
    if (!isOpen) return;
    if (socket && isSocketUuid(socket.socket_id)) {
      setSelectedSocket(socket);
      return;
    }
    if (spaceId) {
      const loadDefaultSocket = async () => {
        setSocketLoading(true);
        setSocketError(null);
        try {
          const defaultSocket = await getOrCreateDefaultSocket(spaceId, integrationRegistry.chrome_extension);
          setSelectedSocket(isSocketUuid(defaultSocket?.socket_id) ? defaultSocket : null);
        } catch (error) {
          console.error('Failed to get or create default socket:', error);
          setSelectedSocket(null);
          setSocketError(error instanceof Error ? error.message : 'Failed to load socket');
        } finally {
          setSocketLoading(false);
        }
      };
      loadDefaultSocket();
    } else {
      setSelectedSocket(null);
    }
  }, [isOpen, socket, spaceId]);
  
  // Permission states
  const [canRead, setCanRead] = useState(true);
  const [canWrite, setCanWrite] = useState(true);
  const [canSearch, setCanSearch] = useState(true);
  const [canDelete, setCanDelete] = useState(false);
  
  // Extension configuration states
  const [extensionName, setExtensionName] = useState('');
  /** When true, extension name is not auto-synced from integration name or socket fallback. */
  const extensionNameTouchedRef = useRef(false);
  const [selectedIcon, setSelectedIcon] = useState('🧠');
  const [themeColor, setThemeColor] = useState('#007AFF');
  
  const [categories, setCategories] = useState('');
  const [excludeTags, setExcludeTags] = useState('');
  const [minImportance, setMinImportance] = useState(1);
  const [requestsPerHour, setRequestsPerHour] = useState(1000);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdIntegration, setCreatedIntegration] = useState<any>(null);
  
  // Extension generation state
  const [isGeneratingExtension, setIsGeneratingExtension] = useState(false);
  const [generatedExtension, setGeneratedExtension] = useState<{filename: string, download_url: string, size: string, extension_id?: string} | null>(null);

  // Load existing extension info when modal opens with existing integration
  useEffect(() => {
    if (isOpen && existingIntegration?.binding_id) {
      extensionNameTouchedRef.current = true;
      setExistingBindingId(existingIntegration.binding_id);
      setIsLoadingExtension(true);
      setExtensionStatus(null);
      
      const loadExtensionInfo = async () => {
        try {
          const extensionInfo = await apiService.getExtensionInfo(existingIntegration.binding_id);
          setExtensionStatus(extensionInfo.status);
          
          if (extensionInfo.status === 'available' && extensionInfo.extension_id) {
            // Load extension metadata from auth_config if available
            const extensionConfig = existingIntegration.auth_config?.extension_config;
            if (extensionConfig) {
              setGeneratedExtension({
                filename: extensionInfo.extension_filename || extensionConfig.extension_filename || 'extension.zip',
                download_url: extensionInfo.extension_download_url || extensionConfig.extension_download_url || '',
                size: extensionInfo.extension_size || extensionConfig.extension_size || 'Unknown',
                extension_id: extensionInfo.extension_id
              });
              
              // Load extension config from auth_config.extension_config
              // Note: extension_config contains both config (name, icon, theme_color) and metadata (extension_id, etc.)
              if (extensionConfig.name) setExtensionName(extensionConfig.name);
              if (extensionConfig.icon) setSelectedIcon(extensionConfig.icon);
              if (extensionConfig.theme_color) setThemeColor(extensionConfig.theme_color);
            } else {
              // Fallback: construct from extension info
              setGeneratedExtension({
                filename: extensionInfo.extension_filename || 'extension.zip',
                download_url: extensionInfo.extension_download_url || '',
                size: extensionInfo.extension_size || 'Unknown',
                extension_id: extensionInfo.extension_id
              });
            }
          } else if (extensionInfo.status === 'expired') {
            // Extension expired, show regeneration option
            setExtensionStatus('expired');
            // Try to load extension config from auth_config even if file expired
            const extensionConfig = existingIntegration.auth_config?.extension_config;
            if (extensionConfig) {
              if (extensionConfig.name) setExtensionName(extensionConfig.name);
              if (extensionConfig.icon) setSelectedIcon(extensionConfig.icon);
              if (extensionConfig.theme_color) setThemeColor(extensionConfig.theme_color);
            }
          }
        } catch (error) {
          console.error('Failed to load extension info:', error);
          setExtensionStatus('not_found');
        } finally {
          setIsLoadingExtension(false);
        }
      };
      
      loadExtensionInfo();
    } else if (isOpen && !existingIntegration) {
      // Reset state for new integration
      extensionNameTouchedRef.current = false;
      setExtensionName('');
      setExistingBindingId(null);
      setExtensionStatus(null);
      setGeneratedExtension(null);
    }
  }, [isOpen, existingIntegration]);

  // Default extension display name from integration name until the user edits the extension field.
  useEffect(() => {
    if (!isOpen) return;
    if (existingIntegration?.binding_id) return;
    if (extensionNameTouchedRef.current) return;
    const next =
      integrationName.trim() ||
      defaultExtensionDisplayNameFromSocket(selectedSocket);
    setExtensionName(next);
  }, [isOpen, existingIntegration, integrationName, selectedSocket]);

  // Pre-made icon options
  const iconOptions = ['🧠', '💼', '📝', '🔖', '⚡', '🎯', '🏢', '📱', '🌐', '⭐'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const socketId = selectedSocket?.socket_id?.trim() ?? '';
    if (!integrationName.trim() || !extensionName.trim() || !isSocketUuid(socketId)) {
      return;
    }

    setIsCreating(true);
    try {
      const permissions = buildBindingPermissions({
        flags: {
          can_read: canRead,
          can_write: canWrite,
          can_search: canSearch,
          can_delete: canDelete,
        },
        categoriesComma: categories,
        excludeTagsComma: excludeTags,
        minImportance,
        requestsPerHour,
      });

      const auth_config = {
        description: description.trim() || 'Chrome extension integration for web content capture',
        response_format: 'json',
        extension_config: {
          name: extensionName.trim(),
          icon: selectedIcon,
          theme_color: themeColor,
          features: ['article_capture', 'text_selection', 'bookmark_creation']
        }
      };

      const integrationData = {
        integration_name: integrationName,
        integration_type: 'chrome_extension',
        auth_config,
        permissions,
        socket_id: socketId
      };

      console.log('Creating Chrome Extension integration:', integrationData);
      const result = await onCreateIntegration(integrationData);

      // After integration creation, immediately generate the extension
      console.log('Integration created, now generating extension...');
      setIsGeneratingExtension(true);
      
      try {
        const extensionResult = await apiService.generateExtension({
          socket_id: socketId,
          extension_config: {
            name: extensionName.trim(),
            description: description.trim() || `${extensionName.trim()} - Memara Chrome Extension`,
            icon: selectedIcon,
            theme_color: themeColor
          }
        });

        if (extensionResult.success && extensionResult.extension) {
          setGeneratedExtension(extensionResult.extension);
          toast.success(
            t('chromeExtension.integrationsModal.generateSuccessToast', {
              ns: 'integrations',
            }) as string
          );
        } else {
          throw new Error(extensionResult.message || 'Extension generation failed');
        }
      } catch (extensionError) {
        console.error('Extension generation failed:', extensionError);
        // Fall back to success modal if extension generation fails
        setCreatedIntegration(result);
        setShowSuccessModal(true);
        toast.error(
          t('chromeExtension.integrationsModal.integrationCreatedExtensionFailedToast', {
            ns: 'integrations',
          }) as string
        );
      } finally {
        setIsGeneratingExtension(false);
      }
      
    } catch (error) {
      console.error('Failed to create Chrome Extension integration:', error);
      toast.error(t('chromeExtension.messages.creationFailed', { ns: 'integrations' }) as string);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDownloadExtension = async () => {
    if (!generatedExtension) return;
    
    try {
      // Extract download ID from the URL or use extension_id
      const downloadId = generatedExtension.extension_id || generatedExtension.download_url.split('/').pop();
      if (!downloadId) {
        toast.error(
          t('chromeExtension.integrationsModal.downloadInvalidUrl', { ns: 'integrations' }) as string
        );
        return;
      }
      
      // Download extension via UI proxy
      const blob = await apiService.downloadExtension(downloadId);
      
      // Create download link and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = generatedExtension.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(
        t('chromeExtension.integrationsModal.downloadSuccessToast', { ns: 'integrations' }) as string
      );
    } catch (error: any) {
      console.error('Download failed:', error);
      // Check if file expired (404)
      if (error?.response?.status === 404) {
        toast.error(
          t('chromeExtension.integrationsModal.downloadExpiredToast', { ns: 'integrations' }) as string
        );
        setExtensionStatus('expired');
        setGeneratedExtension(null);
      } else {
        toast.error(
          t('chromeExtension.integrationsModal.downloadFailedToast', { ns: 'integrations' }) as string
        );
      }
    }
  };

  const handleRegenerateExtension = async () => {
    const socketId = selectedSocket?.socket_id?.trim() ?? '';
    if (!isSocketUuid(socketId) || !existingBindingId) return;
    
    setIsGeneratingExtension(true);
    try {
      // Generate new extension
      const extensionResult = await apiService.generateExtension({
        socket_id: socketId,
        extension_config: {
          name: extensionName.trim(),
          description: description.trim() || `${extensionName.trim()} - Memara Chrome Extension`,
          icon: selectedIcon,
          theme_color: themeColor
        }
      });

      if (extensionResult.success && extensionResult.extension) {
        setGeneratedExtension(extensionResult.extension);
        setExtensionStatus('available');
        toast.success(
          t('chromeExtension.integrationsModal.regenerateSuccessToast', { ns: 'integrations' }) as string
        );
      } else {
        throw new Error(extensionResult.message || 'Extension generation failed');
      }
    } catch (error) {
      console.error('Extension regeneration failed:', error);
      toast.error(
        t('chromeExtension.integrationsModal.regenerateFailedToast', { ns: 'integrations' }) as string
      );
    } finally {
      setIsGeneratingExtension(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    onClose();
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      if (showSuccessModal) {
        handleSuccessModalClose();
      } else {
        onClose();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        variant="scrollable"
        className={showSuccessModal && createdIntegration ? 'max-w-3xl' : 'max-w-4xl'}
      >
        {showSuccessModal && createdIntegration ? (
          <ChromeExtensionSuccessModalContent
            integration={createdIntegration}
            extensionConfig={{
              name: extensionName,
              icon: selectedIcon,
              themeColor,
            }}
            onClose={handleSuccessModalClose}
          />
        ) : (
          <>
          <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
            <DialogTitle className="flex items-center gap-2">
              <Chrome className="w-5 h-5 text-blue-600" />
              {generatedExtension
                ? t('chromeExtension.integrationsModal.dialogTitleReady', { ns: 'integrations' })
                : t('chromeExtension.title', { ns: 'integrations' })}
            </DialogTitle>
            <DialogDescription>
              {generatedExtension
                ? t('chromeExtension.integrationsModal.dialogDescriptionReady', { ns: 'integrations' })
                : t('chromeExtension.description', { ns: 'integrations' })}
            </DialogDescription>
          </DialogHeader>

          <DialogScrollArea className="px-6 space-y-6">
          {/* Loading Extension Info */}
          {isLoadingExtension && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto" />
                  <p className="text-sm text-blue-700">Loading extension information...</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Extension Expired */}
          {!isLoadingExtension && extensionStatus === 'expired' && !generatedExtension && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <div className="flex justify-center">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                      <AlertCircle className="w-8 h-8 text-orange-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-orange-800 mb-2">
                      Extension File Expired
                    </h3>
                    <p className="text-sm text-orange-700 mb-4">
                      The extension file has expired. Please regenerate it to download again.
                    </p>
                  </div>
                  <div className="pt-2">
                    <Button 
                      onClick={handleRegenerateExtension}
                      className="bg-orange-600 hover:bg-orange-700 w-full"
                      disabled={isGeneratingExtension || !isSocketUuid(selectedSocket?.socket_id)}
                    >
                      {isGeneratingExtension ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Regenerating...
                        </>
                      ) : (
                        <>
                          <Chrome className="w-4 h-4 mr-2" />
                          Regenerate Extension
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Extension Download Success */}
          {!isLoadingExtension && generatedExtension && extensionStatus !== 'expired' && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <div className="flex justify-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-green-800 mb-2">
                      {existingBindingId
                        ? t('chromeExtension.integrationsModal.successHeadingRegenerate', {
                            ns: 'integrations',
                          })
                        : t('chromeExtension.integrationsModal.successHeadingNew', { ns: 'integrations' })}
                    </h3>
                    <p className="text-sm text-green-700 mb-4">
                      {t('chromeExtension.integrationsModal.successDescription', {
                        ns: 'integrations',
                        extensionName,
                      })}
                    </p>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="text-center">
                        <div className="font-medium text-green-800">
                          {t('chromeExtension.integrationsModal.gridFilename', { ns: 'integrations' })}
                        </div>
                        <div className="text-green-600 break-all">{generatedExtension.filename}</div>
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-green-800">
                          {t('chromeExtension.integrationsModal.gridSize', { ns: 'integrations' })}
                        </div>
                        <div className="text-green-600">{generatedExtension.size}</div>
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-green-800">
                          {t('chromeExtension.integrationsModal.gridIcon', { ns: 'integrations' })}
                        </div>
                        <div className="text-2xl">{selectedIcon}</div>
                      </div>
                    </div>
                  </div>
                  <div className="pt-2 space-y-2">
                    <Button 
                      onClick={handleDownloadExtension}
                      className="bg-green-600 hover:bg-green-700 w-full"
                      disabled={isGeneratingExtension}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {t('chromeExtension.integrationsModal.downloadZip', {
                        ns: 'integrations',
                        size: generatedExtension.size,
                      })}
                    </Button>
                    {existingBindingId && (
                      <Button 
                        onClick={handleRegenerateExtension}
                        variant="outline"
                        className="w-full border-green-300 text-green-700 hover:bg-green-100"
                        disabled={isGeneratingExtension || !isSocketUuid(selectedSocket?.socket_id)}
                      >
                        {isGeneratingExtension ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Regenerating...
                          </>
                        ) : (
                          <>
                            <Chrome className="w-4 h-4 mr-2" />
                            Regenerate Extension
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {!generatedExtension && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Configuration */}
            <div className="space-y-6">
              
              {/* Integration Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    {t('chromeExtension.sections.integrationDetails', { ns: 'integrations' })}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {t('chromeExtension.fields.integrationName', { ns: 'integrations' })} *
                    </label>
                    <Input
                      value={integrationName}
                      onChange={(e) => setIntegrationName(e.target.value)}
                      placeholder={t('chromeExtension.placeholders.integrationName', { ns: 'integrations' }) as string}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {t('chromeExtension.fields.description', { ns: 'integrations' })}
                    </label>
                    <Input
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t('chromeExtension.placeholders.description', { ns: 'integrations' }) as string}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Extension Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Chrome className="w-4 h-4" />
                    {t('chromeExtension.sections.extensionConfig', { ns: 'integrations' })}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {t('chromeExtension.fields.extensionName', { ns: 'integrations' })} *
                    </label>
                    <Input
                      value={extensionName}
                      onChange={(e) => {
                        extensionNameTouchedRef.current = true;
                        setExtensionName(e.target.value);
                      }}
                      placeholder={t('chromeExtension.placeholders.extensionName', { ns: 'integrations' }) as string}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('chromeExtension.help.extensionName', { ns: 'integrations' })}
                    </p>
                  </div>

                  {/* Icon Selection */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {t('chromeExtension.fields.icon', { ns: 'integrations' })}
                    </label>
                    <div className="grid grid-cols-5 gap-2">
                      {iconOptions.map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          onClick={() => setSelectedIcon(icon)}
                          className={`p-3 text-xl border-2 rounded-lg hover:border-blue-500 transition-colors ${
                            selectedIcon === icon 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200'
                          }`}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Theme Color */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {t('chromeExtension.fields.themeColor', { ns: 'integrations' })}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={themeColor}
                        onChange={(e) => setThemeColor(e.target.value)}
                        className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <Input
                        value={themeColor}
                        onChange={(e) => setThemeColor(e.target.value)}
                        placeholder="#007AFF"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    {t('chromeExtension.policy.permissions.title', { ns: 'integrations' })}
                  </CardTitle>
                  <p className="text-sm text-gray-600">
                    {t('chromeExtension.policy.permissions.description', { ns: 'integrations' })}
                  </p>
                </CardHeader>
                <CardContent>
                  <MemoryPermissionsFields
                    variant="embedded"
                    value={{
                      can_read: canRead,
                      can_write: canWrite,
                      can_search: canSearch,
                      can_delete: canDelete,
                    }}
                    onChange={(v) => {
                      setCanRead(v.can_read);
                      setCanWrite(v.can_write);
                      setCanSearch(v.can_search);
                      setCanDelete(v.can_delete);
                    }}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    {t('chromeExtension.policy.contentFiltering.title', { ns: 'integrations' })}
                  </CardTitle>
                  <p className="text-sm text-gray-600">
                    {t('chromeExtension.policy.contentFiltering.description', { ns: 'integrations' })}
                  </p>
                </CardHeader>
                <CardContent>
                  <ContentFiltersFields
                    variant="embedded"
                    categories={categories}
                    excludeTags={excludeTags}
                    minImportance={minImportance}
                    onCategoriesChange={setCategories}
                    onExcludeTagsChange={setExcludeTags}
                    onMinImportanceChange={setMinImportance}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    {t('chromeExtension.policy.rateLimiting.title', { ns: 'integrations' })}
                  </CardTitle>
                  <p className="text-sm text-gray-600">
                    {t('chromeExtension.policy.rateLimiting.description', { ns: 'integrations' })}
                  </p>
                </CardHeader>
                <CardContent>
                  <RateLimitFields
                    requestsPerHour={requestsPerHour}
                    onRequestsPerHourChange={setRequestsPerHour}
                    min={1}
                    max={100000}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Preview & Info */}
            <div className="space-y-6">
              
              {/* Extension Preview */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {t('chromeExtension.sections.preview', { ns: 'integrations' })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="bg-white p-3 rounded-lg border mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl">{selectedIcon}</span>
                        <span className="font-medium">{extensionName || 'Extension Name'}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Connected to: {selectedSocket?.socket_name || 'Loading...'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Captures: Article, Selection, Bookmarks
                      </div>
                    </div>
                    
                    {/* Browser mockup */}
                    <div className="bg-gray-100 p-2 rounded text-xs">
                      <div className="bg-white p-2 rounded flex items-center justify-between">
                        <span className="text-gray-400">chrome://extensions/</span>
                        <div 
                          className="w-6 h-6 rounded flex items-center justify-center text-white"
                          style={{ backgroundColor: themeColor }}
                        >
                          {selectedIcon}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Socket Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {t('chromeExtension.sections.socketInfo', { ns: 'integrations' })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">
                        {t('chromeExtension.socketInfo.name', { ns: 'integrations' })}:
                      </span>
                      <Badge variant="secondary">{selectedSocket?.socket_name || 'Loading...'}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">
                        {t('chromeExtension.socketInfo.id', { ns: 'integrations' })}:
                      </span>
                      <code className="text-xs bg-gray-100 px-1 rounded">{selectedSocket?.socket_id || 'Loading...'}</code>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">
                        {t('chromeExtension.socketInfo.type', { ns: 'integrations' })}:
                      </span>
                      <Badge variant="outline">API Socket</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Advanced Socket Selection */}
              {spaceId && (
                <div className="mt-4">
                  <AdvancedSocketSelection
                    integration={integrationRegistry.chrome_extension}
                    spaceId={spaceId}
                    currentSocket={selectedSocket}
                    onSocketChange={setSelectedSocket}
                    disabled={isCreating || isGeneratingExtension}
                  />
                </div>
              )}

              {/* Features Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {t('chromeExtension.sections.features', { ns: 'integrations' })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {[
                      t('chromeExtension.features.articleCapture', { ns: 'integrations' }),
                      t('chromeExtension.features.textSelection', { ns: 'integrations' }),
                      t('chromeExtension.features.bookmarkCreation', { ns: 'integrations' }),
                      t('chromeExtension.features.contextMenu', { ns: 'integrations' }),
                      t('chromeExtension.features.toolbarAction', { ns: 'integrations' })
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          )}

          </DialogScrollArea>
          <DialogFooter variant="scrollable" className="gap-2 px-6 pb-6">
            <Button variant="outline" onClick={onClose} disabled={isCreating || isGeneratingExtension}>
              {generatedExtension
                ? t('chromeExtension.integrationsModal.closeWhenReady', { ns: 'integrations' })
                : t('cancel', { ns: 'common' })}
            </Button>
            {!generatedExtension && (
              <Button
                onClick={(e) => {
                  handleSubmit(e);
                }}
                disabled={isCreating || isGeneratingExtension || !integrationName.trim() || !extensionName.trim() || !isSocketUuid(selectedSocket?.socket_id) || socketLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isCreating ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    {t('chromeExtension.actions.creating', { ns: 'integrations' })}
                  </>
                ) : isGeneratingExtension ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    {t('chromeExtension.integrationsModal.generatingExtension', { ns: 'integrations' })}
                  </>
                ) : (
                  <>
                    <Chrome className="w-4 h-4 mr-2" />
                    {t('chromeExtension.integrationsModal.generateAndDownload', { ns: 'integrations' })}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
